import React, { useEffect, useState } from "react";
import api from "../api";
import { Spinner } from "../components/ui-bits";
import { useToast } from "../hooks/use-toast";
import { Mail, Plug, Check, Loader2, Send, KeyRound, Search, ExternalLink, ShieldCheck } from "lucide-react";

function Card({ title, icon: Icon, desc, badge, children }) {
  return (
    <div className="rounded-xl border border-[hsl(240,6%,91%)] bg-white">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-[hsl(240,6%,92%)]">
        <div className="h-9 w-9 rounded-lg brand-bg-soft flex items-center justify-center shrink-0"><Icon className="h-[18px] w-[18px] text-[hsl(245,50%,48%)]" /></div>
        <div className="flex-1"><div className="flex items-center gap-2"><h2 className="text-[15px] font-semibold">{title}</h2>{badge}</div><p className="text-[12.5px] text-[hsl(240,4%,50%)]">{desc}</p></div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function StatusBadge({ ok }) {
  return ok
    ? <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 text-emerald-700 px-1.5 py-0.5 text-[11px] font-medium"><ShieldCheck className="h-3.5 w-3.5" /> Connected</span>
    : <span className="inline-flex rounded-md bg-[hsl(240,5%,93%)] text-[hsl(240,4%,45%)] px-1.5 py-0.5 text-[11px] font-medium">Not connected</span>;
}

export default function IntegrationsPage() {
  const { toast } = useToast();
  const [settings, setSettings] = useState(null);
  const [tools, setTools] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resendKey, setResendKey] = useState("");
  const [sender, setSender] = useState("");
  const [receivingDomain, setReceivingDomain] = useState("");
  const [signature, setSignature] = useState("");
  const [composioKey, setComposioKey] = useState("");
  const [savingR, setSavingR] = useState(false);
  const [savingC, setSavingC] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [q, setQ] = useState("");

  const load = () => Promise.all([api.integrationSettings(), api.composioTools()])
    .then(([s, t]) => { setSettings(s); setTools(t); setSender(s.sender_email || ""); setReceivingDomain(s.resend_receiving_domain || ""); setSignature(s.email_signature || ""); })
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const saveResend = async () => {
    setSavingR(true);
    try { const s = await api.saveIntegrationSettings({ resend_api_key: resendKey || undefined, sender_email: sender, resend_receiving_domain: receivingDomain, email_signature: signature }); setSettings(s); setResendKey(""); toast({ title: "Resend settings saved" }); }
    finally { setSavingR(false); }
  };
  const saveComposio = async () => {
    setSavingC(true);
    try {
      const s = await api.saveIntegrationSettings({ composio_api_key: composioKey || undefined }); setSettings(s); setComposioKey("");
      const t = await api.composioTools(); setTools(t);
      toast({ title: "Composio connected", description: `${(t.toolkits || []).length} tools available` });
    } finally { setSavingC(false); }
  };
  const sendTest = async () => {
    setTesting(true);
    try { await api.testResend(testTo); toast({ title: "Test email sent", description: testTo }); }
    catch (e) { toast({ title: "Send failed", description: e?.response?.data?.detail || "Check your key", variant: "destructive" }); }
    finally { setTesting(false); }
  };

  if (loading) return <Spinner />;
  const toolkits = (tools?.toolkits || []).filter((t) => (t.name || "").toLowerCase().includes(q.toLowerCase()) || (t.category || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 max-w-[1000px] mx-auto animate-in-up space-y-4">
      <div>
        <h1 className="text-[20px] font-semibold flex items-center gap-2"><Plug className="h-5 w-5 text-[hsl(245,50%,48%)]" /> Integrations</h1>
        <p className="text-[13.5px] text-[hsl(240,4%,48%)] mt-0.5">Connect Resend for real email sending and Composio for the full tool catalog Hermes can operate.</p>
      </div>

      {/* Resend */}
      <Card title="Resend" icon={Mail} desc="Send real outreach emails at their scheduled time." badge={<StatusBadge ok={settings.resend_configured} />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-[13px] font-medium mb-1">API key {settings.resend_configured && <span className="text-[hsl(240,4%,55%)] font-normal font-mono">({settings.resend_api_key_masked})</span>}</label>
            <input type="password" value={resendKey} onChange={(e) => setResendKey(e.target.value)} placeholder="re_..." className="w-full rounded-md border border-[hsl(240,6%,88%)] px-3 py-2 text-[14px] outline-none focus:ring-2 focus:ring-[hsl(245,58%,70%)]" />
          </div>
          <div>
            <label className="block text-[13px] font-medium mb-1">Sender email</label>
            <input value={sender} onChange={(e) => setSender(e.target.value)} placeholder="you@yourdomain.com" className="w-full rounded-md border border-[hsl(240,6%,88%)] px-3 py-2 text-[14px] outline-none focus:ring-2 focus:ring-[hsl(245,58%,70%)]" />
          </div>
          <div>
            <label className="block text-[13px] font-medium mb-1">Receiving domain</label>
            <input value={receivingDomain} onChange={(e) => setReceivingDomain(e.target.value)} placeholder="reply.yourdomain.com" className="w-full rounded-md border border-[hsl(240,6%,88%)] px-3 py-2 text-[14px] outline-none focus:ring-2 focus:ring-[hsl(245,58%,70%)]" />
            <p className="text-[11px] text-[hsl(240,4%,55%)] mt-1">Resend routes customer replies to this domain.</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-[13px] font-medium mb-1">Email signature</label>
            <textarea value={signature} onChange={(e) => setSignature(e.target.value)} rows={3} placeholder="Alex Rivera&#10;Tokenbuds" className="w-full resize-y rounded-md border border-[hsl(240,6%,88%)] px-3 py-2 text-[14px] outline-none focus:ring-2 focus:ring-[hsl(245,58%,70%)]" />
            <p className="text-[11px] text-[hsl(240,4%,55%)] mt-1">Choose “Add signature” in Mailbox when sending a reply.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={saveResend} disabled={savingR} className="rounded-md brand-bg text-white px-3 py-1.5 text-[13px] font-medium hover:brightness-105 inline-flex items-center gap-1.5">{savingR && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save</button>
          <div className="ml-auto flex items-center gap-2">
            <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="send test to…" className="rounded-md border border-[hsl(240,6%,88%)] px-2.5 py-1.5 text-[13px] outline-none w-[220px]" />
            <button onClick={sendTest} disabled={!settings.resend_configured || !testTo || testing} className="rounded-md border border-[hsl(240,6%,88%)] px-3 py-1.5 text-[13px] font-medium hover:bg-[hsl(240,5%,96%)] disabled:opacity-50 inline-flex items-center gap-1.5">{testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Test</button>
          </div>
        </div>
        {!settings.resend_configured && <p className="text-[12px] text-[hsl(240,4%,55%)] mt-2">Until a Resend API key is configured (env or saved here), sends fail honestly — steps stay unsent.</p>}
      </Card>

      {/* Composio */}
      <Card title="Composio" icon={KeyRound} desc="One API key unlocks hundreds of tools Hermes can operate." badge={<StatusBadge ok={settings.composio_configured} />}>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-[13px] font-medium mb-1">API key {settings.composio_configured && <span className="text-[hsl(240,4%,55%)] font-normal font-mono">({settings.composio_api_key_masked})</span>}</label>
            <input type="password" value={composioKey} onChange={(e) => setComposioKey(e.target.value)} placeholder="comp_..." className="w-full rounded-md border border-[hsl(240,6%,88%)] px-3 py-2 text-[14px] outline-none focus:ring-2 focus:ring-[hsl(245,58%,70%)]" />
          </div>
          <button onClick={saveComposio} disabled={savingC} className="rounded-md brand-bg text-white px-3 py-1.5 text-[13px] font-medium hover:brightness-105 inline-flex items-center gap-1.5">{savingC && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Connect</button>
        </div>
        {tools?.error && <p className="text-[12px] text-amber-600 mt-2">Couldn't reach Composio ({tools.error}). Showing popular tools below.</p>}
      </Card>

      {/* Tool catalog */}
      <div className="rounded-xl border border-[hsl(240,6%,91%)] bg-white">
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[hsl(240,6%,92%)]">
          <h2 className="text-[14px] font-semibold">Tool catalog</h2>
          <span className="text-[12px] text-[hsl(240,4%,55%)]">{tools?.configured ? "Live from Composio" : "Popular tools (connect to load the full list)"}</span>
          <div className="ml-auto flex items-center gap-1.5 rounded-md border border-[hsl(240,6%,90%)] px-2 py-[5px] w-[220px]">
            <Search className="h-[15px] w-[15px] text-[hsl(240,4%,55%)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tools…" className="w-full bg-transparent text-[13px] outline-none" />
          </div>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
          {toolkits.map((t) => (
            <div key={t.slug} className="rounded-lg border border-[hsl(240,6%,91%)] p-3 hover:shadow-sm transition-shadow">
              <div className="flex items-center gap-2">
                {t.logo ? <img src={t.logo} alt="" className="h-7 w-7 rounded" /> : <div className="h-7 w-7 rounded bg-[hsl(245,58%,92%)] text-[hsl(245,50%,45%)] flex items-center justify-center text-[12px] font-semibold">{(t.name || "?").slice(0, 1)}</div>}
                <div className="min-w-0"><div className="text-[13px] font-medium truncate">{t.name}</div><div className="text-[11px] text-[hsl(240,4%,55%)]">{t.category}</div></div>
              </div>
              {t.description && <p className="text-[11.5px] text-[hsl(240,4%,50%)] mt-2 line-clamp-2">{t.description}</p>}
            </div>
          ))}
          {toolkits.length === 0 && <div className="col-span-full text-center py-8 text-[13px] text-[hsl(240,4%,55%)]">No tools match "{q}".</div>}
        </div>
      </div>
    </div>
  );
}
